import { computed, onMounted, shallowRef } from "vue";
import { useI18n } from "vue-i18n";
import { useFetch } from "@/core/api/common";
import { globals } from "@/core/globals";
import { Logger } from "@/core/utilities";
import type { IOptiCountry } from "@/core/types/opti-country";

export const ALL_COUNTRIES_OPTION_ID = "AllCountries";

export function useOptiCountries(includeAllCountriesOption?: boolean) {
  const { t } = useI18n();
  const countries = shallowRef<IOptiCountry[]>([]);

  const fetchCountries = async () => {
    try {
      const { data, error: fetchError } = await useFetch(`${globals.optimizelyUrl}/api/countries`).json<
        IOptiCountry[]
      >();

      if (fetchError?.value || !data?.value || !Array.isArray(data.value)) {
        throw new Error("Failed to fetch valid countries");
      }

      countries.value = data.value;

      if (includeAllCountriesOption) {
        countries.value.unshift({
          id: ALL_COUNTRIES_OPTION_ID,
          name: t("pages.locations.all_locations"),
        });
      }
    } catch (fetchThrownError) {
      Logger.error("useOptiCountries", fetchThrownError);
    }
  };

  const countryMap = computed(() =>
    countries.value.reduce<{ [key: string]: IOptiCountry }>((acc, country) => {
      acc[country.id] = country;
      return acc;
    }, {}),
  );
  const getCountryById = (id: string): IOptiCountry | undefined => countryMap.value[id];

  onMounted(() => {
    void fetchCountries();
  });

  return {
    countries,
    fetchCountries,
    getCountryById,
  };
}
